import type { DataProvider, Identifier } from "ra-core";

import type { Contact } from "../types";

export const PLANNED_DEAL_STAGE = "planejada";

const CONTACTS_PAGE_SIZE = 200;

export function contactHasAllTags(
  contactTags: readonly (string | number)[] | null | undefined,
  requiredTagIds: readonly (string | number)[],
): boolean {
  if (requiredTagIds.length === 0) return false;
  const owned = new Set((contactTags ?? []).map((id) => String(id)));
  return requiredTagIds.every((id) => owned.has(String(id)));
}

export function tagsContainsFilter(tagIds: readonly (string | number)[]): {
  "tags@cs": string;
} {
  return { "tags@cs": `{${tagIds.join(",")}}` };
}

export async function fetchMatchingContacts(
  dataProvider: DataProvider,
  tagIds: readonly Identifier[],
): Promise<Contact[]> {
  if (tagIds.length === 0) return [];

  const filter = {
    "archived_at@is": null,
    ...tagsContainsFilter(tagIds),
  };
  const matches: Contact[] = [];
  let page = 1;

  for (;;) {
    const { data, total } = await dataProvider.getList<Contact>("contacts", {
      filter,
      pagination: { page, perPage: CONTACTS_PAGE_SIZE },
      sort: { field: "last_name", order: "ASC" },
    });

    matches.push(
      ...data.filter(
        (contact) =>
          contact.archived_at == null &&
          contactHasAllTags(contact.tags, tagIds),
      ),
    );

    const fetched = page * CONTACTS_PAGE_SIZE;
    if (
      data.length < CONTACTS_PAGE_SIZE ||
      (total != null && fetched >= total)
    ) {
      break;
    }
    page += 1;
  }

  return matches.sort((a, b) => {
    const byCompany = (a.company_name ?? "").localeCompare(
      b.company_name ?? "",
    );
    if (byCompany !== 0) return byCompany;
    return `${a.last_name} ${a.first_name}`.localeCompare(
      `${b.last_name} ${b.first_name}`,
    );
  });
}

export type ProposalDraft = {
  name: string;
  company_id: Identifier;
  contact_ids: Identifier[];
  stage: typeof PLANNED_DEAL_STAGE;
};

type GroupableContact = {
  id: Identifier;
  company_id?: Identifier | null;
  company_name?: string | null;
};

export function proposalName(campaignName: string, companyName: string) {
  return `${campaignName} — ${companyName}`;
}

export function buildProposalDrafts(
  contacts: readonly GroupableContact[],
  campaignName: string,
): ProposalDraft[] {
  const groups = new Map<string, ProposalDraft>();

  for (const contact of contacts) {
    if (contact.company_id == null || contact.company_id === "") continue;
    const key = String(contact.company_id);
    const existing = groups.get(key);
    if (existing) {
      existing.contact_ids.push(contact.id);
      continue;
    }
    groups.set(key, {
      name: proposalName(campaignName, contact.company_name?.trim() || key),
      company_id: contact.company_id,
      contact_ids: [contact.id],
      stage: PLANNED_DEAL_STAGE,
    });
  }

  return [...groups.values()];
}
